/**
 * ChargeListContainer
 *
 * --
 *
 */
import { useEffect, useState } from 'react';
import ChargeListPresenter from './ChargeListPresenter';
import {
  ShopInformationApi,
  ProductCategoryApi,
  ProductApi,
  MapProductPolicyApi,
  ProductPolicyApi,
} from 'api';
import MessageAlert from 'utils';

/**----------------------------------------
 * [Component] 수수료관리: 컨테이너
 * ----------------------------------------
 */
const ChargeListContainer = () => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== STATE ===== */
  // 로딩
  const [loading, setLoading] = useState(false);
  // 선택된 업체의 키
  const [currentShop, setCurrentShop] = useState(null);
  // 업체목록
  const [shopList, setShopList] = useState([]);
  // 카테고리목록
  const [categoryList, setCategoryList] = useState([]);
  // 상품목록
  const [productList, setProductList] = useState([]);
  // 선택된 카테고리
  const [selectedCategory, setSelectedCategory] = useState(null);
  // 선택된 상품
  const [selectedProduct, setSelectedProduct] = useState(null);
  // 선택된 상품
  const [productPolicy, setProductPolicy] = useState(null);

  /* ===== VARIABLE ===== */
  /* 선택된 업체 */
  const shop = currentShop
    ? shopList.filter((item) => item.shop_information_id === currentShop)[0]
    : null;
  /* ===== FUNCTION ===== */
  /**--------------------
   * 업체목록 조회
   * --------------------
   */
  const getShopInformations = async () => {
    try {
      const { status, message, data } =
        await ShopInformationApi.getShopInformations();
      if (status !== 200) throw { message };
      setShopList(
        data.filter((item) => item.type === 'ALL' || item.type === 'SHOP')
        // data.filter((item) => item.type === 'ALL' || item.is_brand === 'Y')
      );
    } catch (e) {
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 메뉴목록 조회
   * --------------------
   */
  const getMenuList = () => {
    if (!currentShop) return MessageAlert.warning('업체를 선택해주세요');
    setLoading(true);
    Promise.all([
      ProductCategoryApi.getProductCategories(),
      ProductApi.getProducts({
        join_tables: `["map_product_policy"]`,
        shop_information_id: currentShop,
      }),
    ])
      .then((resp) => {
        const categoryResult = resp[0];
        const productResult = resp[1];

        setTimeout(() => {
          setCategoryList(categoryResult.data);
          setProductList(productResult.data);
          setLoading(false);
        }, 500);
      })
      .catch((e) => {
        setLoading(false);
        MessageAlert.error(e.message);
      });
  };

  /**--------------------
   * 상품 상세조회
   * --------------------
   */
  const getProductDetail = async () => {
    setLoading(true);
    try {
      const { status, message, data } =
        await MapProductPolicyApi.getMapProductPolicies({
          join_tables: `["product_policy"]`,
          product_id: selectedProduct.product_id,
        });
      if (status !== 200) throw { message };

      setTimeout(() => {
        setProductPolicy(data[0]);
        setLoading(false);
      }, 750);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /**--------------------
   * 수수료 저장 함수
   * --------------------
   */
  const handleSaveCommission = async (newCommission) => {
    setLoading(true);
    try {
      const { commission_rate, commission_type } = newCommission;
      let newData, result;
      // 상품 정책 변경
      if (selectedProduct) {
        // 데이터가 있는경우 => 수정
        if (productPolicy) {
          result = await ProductPolicyApi.updateProductPolicy({
            product_policy_id: productPolicy.product_policy_id,
            commission_rate,
            commission_type,
          });
        }
        // 데이터가 없는경우 => 생성
        else {
          // ProductPolicy 생성
          result = await ProductPolicyApi.createProductPolicy({
            commission_rate,
            commission_type,
          });
          // MapProductPolicy 생성
          result = await MapProductPolicyApi.createMapProductPolicy({
            product_policy_id: result.data.product_policy_id,
            product_id: selectedProduct.product_id,
            policy_status: 'POSSIBLE',
            is_enable: 'Y',
          });
          // 정책데이터 업데이트
          getProductDetail();
        }
      }
      // 카테고리 정책 변경
      else if (selectedCategory) {
        result = await ProductCategoryApi.updateProductCategory({
          product_category_id: selectedCategory.product_category_id,
          commission_rate,
          commission_type,
        });

        getMenuList();
      }
      // 업체정책 변경
      else if (shop) {
        result = await ShopInformationApi.updateShopInformation({
          shop_information_id: currentShop,
          commission_rate,
          commission_type,
        });
        getShopInformations();
      } else {
        MessageAlert.warning('업체 데이터를 선택해주세요');
        setLoading(false);
        return;
      }

      // 예외처리
      const { status, message } = result;
      if (status !== 200) throw { message };

      setTimeout(() => {
        MessageAlert.info('저장되었습니다.');
        setLoading(false);
      }, 750);
    } catch (e) {
      setLoading(false);
      MessageAlert.error(e.message);
    }
  };

  /* ===== HOOKS ===== */
  /* 최초 접속 시 업체목록 조회 */
  useEffect(() => {
    getShopInformations();
  }, []);

  /* 업체 변경 시 실행 */
  useEffect(() => {
    currentShop && getMenuList();
    setSelectedCategory(null);
  }, [currentShop]);

  /* 카테고리 변경 시 선택한 상품 초기화 */
  useEffect(() => {
    setSelectedProduct(null);
    setProductPolicy(null);
  }, [selectedCategory]);

  /* 상품 선택 시 상세조회 */
  useEffect(() => {
    selectedProduct && getProductDetail();
  }, [selectedProduct]);

  /* ===== RENDER ===== */
  return (
    <>
      <ChargeListPresenter
        shopList={shopList}
        currentShop={currentShop}
        setCurrentShop={setCurrentShop}
        categoryList={categoryList}
        productList={productList}
        {...{
          selectedCategory,
          setSelectedCategory,
          selectedProduct,
          setSelectedProduct,
          productPolicy,
          loading,
          onSaveCommission: handleSaveCommission,
          shop,
        }}
      />
    </>
  );
};

export default ChargeListContainer;
